import {
  WbNoData,
  getOnHoverScrollbarStyle,
  useElementSize,
} from '@agilelab/plugin-wb-platform';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { NodeProps, useUpdateNodeInternals } from 'reactflow';
import { BusinessConcept as BusinessConceptComponent } from './components/BusinessConcept';

import { PrototypeCard } from '../../../components/PrototypeCard';
import { PrototypeSection } from '../../../components/PrototypeSection';
import { usePrototypeInformationGraphContext } from '../../../PrototypeInformationGraphContext';
import { groupByDomain } from '../../../utils';
import { BusinessConceptsEmptyStateIcon } from './icons/Icons';

export type BusinessConceptsData = {
  style: CSSProperties;
};

export type BusinessConceptsProps = NodeProps<BusinessConceptsData>;

export const BUSINESS_CONCEPTS_NODE_ID = 'business_concepts';

const SCROLL_BAR_WIDTH = 13;

const useStyles = makeStyles(
  theme => ({
    scroll: {
      height: '100%',
      width: '100%',
      overflowY: 'scroll',
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      ...getOnHoverScrollbarStyle(theme, false),
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      justifyContent: 'center',
    },
    actionsContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItem: 'center',
    },
    aiIcon: {
      color: theme.palette.accent.main,
    },
    header: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(2.5),
    },
    card: {
      marginRight: theme.spacing(3) - SCROLL_BAR_WIDTH,
    },
    emptyState: {
      width: '100%',
      maxWidth: '125px',
    },
  }),
  { name: 'BusinessConcepts' },
);

export const BusinessConceptsContent = ({ id }: BusinessConceptsProps) => {
  const classes = useStyles();

  const {
    prototype: { businessConcepts },
  } = usePrototypeInformationGraphContext();
  const updateNodeInternals = useUpdateNodeInternals();

  const onScroll = useCallback(() => {
    updateNodeInternals(id);
  }, [updateNodeInternals, id]);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { clientHeight } = useElementSize(contentRef, 30);

  useEffect(() => {
    updateNodeInternals(id);
  }, [updateNodeInternals, clientHeight, id, businessConcepts]);

  const businessConceptsByDomain = useMemo(
    () => groupByDomain(businessConcepts ?? []),
    [businessConcepts],
  );

  return (
    <div ref={scrollRef} className={clsx(classes.scroll)} onScroll={onScroll}>
      <div ref={contentRef} className={classes.content}>
        {Object.entries(businessConceptsByDomain).map(([domain, items]) => (
          <BusinessConceptComponent
            parentRef={scrollRef}
            key={domain}
            name={domain}
            businessConcepts={items}
          />
        ))}
      </div>
    </div>
  );
};

export const BusinessConcepts = (props: BusinessConceptsProps) => {
  const {
    prototype: { businessConcepts },
  } = usePrototypeInformationGraphContext();
  const classes = useStyles();

  const emptyState = (
    <PrototypeCard style={{ height: '100%' }} className={classes.card}>
      <WbNoData
        fillContainer
        text="Expose relevant Business Concepts in the output of your prototype"
        icon={<BusinessConceptsEmptyStateIcon className={classes.emptyState} />}
      />
    </PrototypeCard>
  );

  return (
    <>
      <PrototypeSection
        title="Business Concepts"
        className={clsx('nowheel', 'nodrag')}
        headerClassName={classes.header}
        style={{
          ...props.data.style,
          paddingLeft: 0,
          paddingRight: 24 - SCROLL_BAR_WIDTH,
        }}
      >
        {businessConcepts?.length ? (
          <BusinessConceptsContent {...props} />
        ) : (
          emptyState
        )}
      </PrototypeSection>
    </>
  );
};
